'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface FooterProps {
  currentLanguage: string;
}

const Footer = ({ currentLanguage }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2025');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const content = {
    en: {
      tagline: 'Making Carbon Credits Simple',
      quickLinks: 'Quick Links',
      services: 'Services',
      resources: 'Resources',
      contact: 'Contact',
      newsletter: 'Newsletter',
      newsletterText: 'Subscribe for sustainability insights and updates',
      emailPlaceholder: 'Enter your email',
      subscribeButton: 'Subscribe',
      copyright: 'GenCarbon ESG. All rights reserved.',
      dpdpPrivacy: 'Privacy Maintained Under DPDP Act 2023',
      links: {
        about: 'About Dr. Nupur',
        carbonCredits: 'Carbon Credits',
        ourServices: 'Our Services',
        impact: 'Impact Stories',
        getStarted: 'Get Started'
      },
      serviceLinks: {
        carbon: 'Carbon Credit Generation',
        esg: 'ESG Reporting',
        water: 'Water Conservation'
      },
      contactInfo: {
        email: 'nupurbanker@gmail.com',
        phone: '+91 9901503669',
        whatsapp: '+91 9901503669',
        googleMeet: 'nupurbanker',
        website: 'www.gencarbon.in',
        address: 'Hyderabad (HQ), Vizag, Bangalore, Mumbai'
      }
    },
    hi: {
      tagline: 'कार्बन क्रेडिट को सरल बनाना',
      quickLinks: 'त्वरित लिंक',
      services: 'सेवाएं',
      resources: 'संसाधन',
      contact: 'संपर्क',
      newsletter: 'न्यूज़लेटर',
      newsletterText: 'स्थिरता अंतर्दृष्टि और अपडेट के लिए सदस्यता लें',
      emailPlaceholder: 'अपना ईमेल दर्ज करें',
      subscribeButton: 'सदस्यता लें',
      copyright: 'GenCarbon ESG। सर्वाधिकार सुरक्षित।',
      dpdpPrivacy: 'DPDP अधिनियम 2023 के तहत गोपनीयता बनाए रखी गई',
      links: {
        about: 'डॉ. नूपुर के बारे में',
        carbonCredits: 'कार्बन क्रेडिट',
        ourServices: 'हमारी सेवाएं',
        impact: 'प्रभाव कहानियां',
        getStarted: 'शुरू करें'
      },
      serviceLinks: {
        carbon: 'कार्बन क्रेडिट जनरेशन',
        esg: 'ESG रिपोर्टिंग',
        water: 'जल संरक्षण'
      },
      contactInfo: {
        email: 'nupurbanker@gmail.com',
        phone: '+91 9901503669',
        whatsapp: '+91 9901503669',
        googleMeet: 'nupurbanker',
        website: 'www.gencarbon.in',
        address: 'हैदराबाद (HQ), विशाखापत्तनम, बैंगलोर, मुंबई'
      }
    },
    ar: {
      tagline: 'تبسيط أرصدة الكربون',
      quickLinks: 'روابط سريعة',
      services: 'الخدمات',
      resources: 'الموارد',
      contact: 'اتصل بنا',
      newsletter: 'النشرة الإخبارية',
      newsletterText: 'اشترك للحصول على رؤى الاستدامة والتحديثات',
      emailPlaceholder: 'أدخل بريدك الإلكتروني',
      subscribeButton: 'اشترك',
      copyright: 'GenCarbon ESG. جميع الحقوق محفوظة.',
      dpdpPrivacy: 'الخصوصية المحافظة عليها بموجب قانون DPDP 2023',
      links: {
        about: 'عن الدكتورة نوبور',
        carbonCredits: 'أرصدة الكربون',
        ourServices: 'خدماتنا',
        impact: 'قصص التأثير',
        getStarted: 'ابدأ'
      },
      serviceLinks: {
        carbon: 'توليد أرصدة الكربون',
        esg: 'تقارير ESG',
        water: 'الحفاظ على المياه'
      },
      contactInfo: {
        email: 'nupurbanker@gmail.com',
        phone: '+91 9901503669',
        whatsapp: '+91 9901503669',
        googleMeet: 'nupurbanker',
        website: 'www.gencarbon.in',
        address: 'حيدر أباد (المقر الرئيسي)، فيزاغ، بنغالور، مومباي'
      }
    },
    te: {
      tagline: 'కార్బన్ క్రెడిట్లను సులభం చేయడం',
      quickLinks: 'త్వరిత లింక్‌లు',
      services: 'సేవలు',
      resources: 'వనరులు',
      contact: 'సంప్రదించండి',
      newsletter: 'న్యూస్‌లెటర్',
      newsletterText: 'సస్టైనబిలిటీ అంతర్దృష్టులు మరియు అప్‌డేట్‌ల కోసం సబ్‌స్క్రైబ్ చేయండి',
      emailPlaceholder: 'మీ ఇమెయిల్ నమోదు చేయండి',
      subscribeButton: 'సబ్‌స్క్రైబ్ చేయండి',
      copyright: 'GenCarbon ESG. అన్ని హక్కులు రిజర్వ్ చేయబడ్డాయి.',
      dpdpPrivacy: 'DPDP చట్టం 2023 క్రింద గోప్యత నిర్వహించబడుతుంది',
      links: {
        about: 'డాక్టర్ నూపూర్ గురించి',
        carbonCredits: 'కార్బన్ క్రెడిట్స్',
        ourServices: 'మా సేవలు',
        impact: 'ప్రభావ కథనాలు',
        getStarted: 'ప్రారంభించండి'
      },
      serviceLinks: {
        carbon: 'కార్బన్ క్రెడిట్ జనరేషన్',
        esg: 'ESG రిపోర్టింగ్',
        water: 'నీటి పరిరక్షణ'
      },
      contactInfo: {
        email: 'nupurbanker@gmail.com',
        phone: '+91 9901503669',
        whatsapp: '+91 9901503669',
        googleMeet: 'nupurbanker',
        website: 'www.gencarbon.in',
        address: 'హైదరాబాద్ (HQ), విజయవాడ, బెంగళూరు, ముంబై'
      }
    },
    bn: {
      tagline: 'কার্বন ক্রেডিট সহজ করা',
      quickLinks: 'দ্রুত লিঙ্ক',
      services: 'সেবা',
      resources: 'সম্পদ',
      contact: 'যোগাযোগ',
      newsletter: 'নিউজলেটার',
      newsletterText: 'টেকসই অন্তর্দৃষ্টি এবং আপডেটের জন্য সাবস্ক্রাইব করুন',
      emailPlaceholder: 'আপনার ইমেইল লিখুন',
      subscribeButton: 'সাবস্ক্রাইব করুন',
      copyright: 'GenCarbon ESG। সর্বস্বত্ব সংরক্ষিত।',
      dpdpPrivacy: 'DPDP আইন 2023 এর অধীনে গোপনীয়তা বজায় রাখা হয়েছে',
      links: {
        about: 'ডাঃ নূপুর সম্পর্কে',
        carbonCredits: 'কার্বন ক্রেডিট',
        ourServices: 'আমাদের সেবা',
        impact: 'প্রভাব গল্প',
        getStarted: 'শুরু করুন'
      },
      serviceLinks: {
        carbon: 'কার্বন ক্রেডিট জেনারেশন',
        esg: 'ESG রিপোর্টিং',
        water: 'জল সংরক্ষণ'
      },
      contactInfo: {
        email: 'nupurbanker@gmail.com',
        phone: '+91 9901503669',
        whatsapp: '+91 9901503669',
        googleMeet: 'nupurbanker',
        website: 'www.gencarbon.in',
        address: 'হায়দরাবাদ (HQ), ভিজাগ, ব্যাঙ্গালোর, মুম্বাই'
      }
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 mb-6 md:mb-8">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-4">
            <Link href="/homepage" className="flex items-center space-x-2">
              <Image
                src="/assets/images/gencarbon_logo-1767797913709.png"
                alt="GenCarbon ESG logo with Go Green Earn Green tagline"
                width={180}
                height={50}
                className="h-12 w-auto object-contain"
                priority
              />
            </Link>
            <p className="text-sm text-text-secondary max-w-xs">
              {currentContent.tagline}
            </p>
            <div className="flex space-x-3">
              <a
                href="https://www.linkedin.com/in/nupurmy/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-surface flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-surface flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
              >
                <Icon name="ShareIcon" size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-heading font-semibold text-text-primary">
              {currentContent.quickLinks}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  href="/about-dr-nupur"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.links.about}
                </Link>
              </li>
              <li>
                <Link
                  href="/carbon-credits-explained"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.links.carbonCredits}
                </Link>
              </li>
              <li>
                <Link
                  href="/our-services"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.links.ourServices}
                </Link>
              </li>
              <li>
                <Link
                  href="/impact-stories"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.links.impact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="font-heading font-semibold text-text-primary">
              {currentContent.services}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  href="/our-services"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.serviceLinks.carbon}
                </Link>
              </li>
              <li>
                <Link
                  href="/our-services"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.serviceLinks.esg}
                </Link>
              </li>
              <li>
                <Link
                  href="/our-services"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.serviceLinks.water}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-heading font-semibold text-text-primary">
              {currentContent.contact}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <Icon name="EnvelopeIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <a
                  href={`tel:${currentContent.contactInfo.phone.replace(/[\s-]/g, '')}`}
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.contactInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="PhoneIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <a
                  href={`https://wa.me/${currentContent.contactInfo.whatsapp.replace(/[\s-]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  {currentContent.contactInfo.whatsapp}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="VideoCameraIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <a
                  href={`https://meet.google.com/${currentContent.contactInfo.googleMeet}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
                >
                  Google Meet: {currentContent.contactInfo.googleMeet}
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="MapPinIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-text-secondary">
                  {currentContent.contactInfo.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col items-center md:items-start space-y-2">
              <p className="text-sm text-text-secondary">
                © {isHydrated ? currentYear : '2025'} {currentContent.copyright}
              </p>
              <a
                href="mailto:nupurbanker@gmail.com"
                className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 flex items-center gap-1"
              >
                <Icon name="EnvelopeIcon" size={14} />
                nupurbanker@gmail.com
              </a>
            </div>
            <div className="flex items-center space-x-6">
              <Link
                href="/privacy-policy"
                className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-sm text-text-secondary hover:text-primary transition-colors duration-200"
              >
                Terms of Service
              </Link>
              <div className="flex items-center space-x-2 px-3 md:px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                <Icon name="ShieldCheckIcon" size={16} className="text-green-600" />
                <span className="text-xs font-semibold text-green-800">
                  {currentContent.dpdpPrivacy}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;